import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import s from "./StoryPanel.module.css";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function PlayIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.347a1.125 1.125 0 0 1 0 1.972l-11.54 6.347a1.125 1.125 0 0 1-1.667-.986V5.653Z" />
    </svg>
  );
}

function SlowPlayIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M21 7.5V18M15 7.5V18M3 16.811V8.69c0-.864.933-1.406 1.683-.977l7.108 4.061a1.125 1.125 0 0 1 0 1.954l-7.108 4.061A1.125 1.125 0 0 1 3 16.811Z" />
    </svg>
  );
}

function QuizIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 5.25h.008v.008H12v-.008Z" />
    </svg>
  );
}

function CheckIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 13l4 4L19 7" />
    </svg>
  );
}

function StarIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    </svg>
  );
}

function useAudioPlayer() {
  const audioRef = useRef(null);

  const play = useCallback((storyId, audioPath, playbackRate = 1.0) => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    const url = `${BACKEND}/api/v1/admin/audio/${storyId}/${audioPath}`;
    const audio = new Audio(url);
    audio.playbackRate = playbackRate;
    audio.play().catch(() => {
      alert("Audio not generated yet.");
    });
    audioRef.current = audio;
  }, []);

  const stop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);

  return { play, stop };
}

function useWordAudio() {
  const audioRef = useRef(null);
  const generationRef = useRef(0);
  const [loadingWordId, setLoadingWordId] = useState(null);

  const playWord = useCallback((wordId, filename, playbackRate = 1.0) => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    const generation = ++generationRef.current;
    const key = `${wordId}/${filename}`;
    setLoadingWordId(key);

    const clear = () => {
      if (generationRef.current === generation) setLoadingWordId(null);
    };

    const url = `${BACKEND}/api/v1/admin/word-audio/${wordId}/${filename}`;
    const audio = new Audio(url);
    audio.playbackRate = playbackRate;
    audioRef.current = audio;

    audio.addEventListener("ended", clear);
    audio.addEventListener("error", () => { clear(); alert("Failed to play word audio."); });
    audio.play().catch(() => { clear(); alert("Failed to play word audio."); });
  }, []);

  const stop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    generationRef.current++;
    setLoadingWordId(null);
  }, []);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);

  return { playWord, stop, loadingWordId };
}


function SpinnerIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    </svg>
  );
}

function Tooltip({ wordData, position, onClose, pinned, onPlayWord, onPlayExample, loadingWordId }) {
  const wordId = wordData.id;

  const hasAudio = !!wordId;
  const isWordLoading = loadingWordId === `${wordId}/word_in_swedish.mp3`;
  const isExampleLoading = loadingWordId === `${wordId}/example_in_swedish.mp3`;

  return createPortal(
    <div
      className={s.tooltip}
      style={{
        position: "fixed",
        top: position.top,
        left: position.left,
        transform: "translateX(-50%) translateY(-100%)",
      }}
    >
      {/* gold accent stripe */}
      <div className={s.tooltipAccent} />

      <div className={s.tooltipBody}>
        {/* -- Header row -- */}
        <div className={s.tooltipHeader}>
          <div className={s.tooltipHeaderLeft}>
            <span className={s.tooltipWord}>{wordData.word}</span>
          </div>
          <div className={s.tooltipActions}>
            <button
              className={s.tooltipPlayBtn}
              title="Play word"
              disabled={!hasAudio || isWordLoading}
              onClick={(e) => { e.stopPropagation(); onPlayWord(wordId, 1.0); }}
            >
              {isWordLoading
                ? <SpinnerIcon className={`${s.tooltipPlayIcon} ${s.spinning}`} />
                : <PlayIcon className={s.tooltipPlayIcon} />}
            </button>
            <button
              className={s.tooltipPlayBtn}
              title="Slow play word"
              disabled={!hasAudio || isWordLoading}
              onClick={(e) => { e.stopPropagation(); onPlayWord(wordId, 0.7); }}
            >
              {isWordLoading
                ? <SpinnerIcon className={`${s.tooltipPlayIcon} ${s.spinning}`} />
                : <SlowPlayIcon className={s.tooltipPlayIcon} />}
            </button>
            {pinned && (
              <button className={s.tooltipCloseBtn} onClick={onClose} title="Close">
                <svg className={s.tooltipCloseIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M6 18 18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* -- Meaning -- */}
        <span className={s.tooltipMeaning}>{wordData.meaning}</span>

        {/* -- Description -- */}
        <p className={s.tooltipDesc}>{wordData.description}</p>

        {/* -- Example section -- */}
        <div className={s.tooltipExampleCard}>
          <div className={s.tooltipExampleRow}>
            <span className={s.tooltipExampleLabel}>Example</span>
            <button
              className={s.tooltipPlayBtnSmall}
              title="Play example"
              disabled={!hasAudio || isExampleLoading}
              onClick={(e) => { e.stopPropagation(); onPlayExample(wordId, 1.0); }}
            >
              {isExampleLoading
                ? <SpinnerIcon className={`${s.tooltipPlayIconSmall} ${s.spinning}`} />
                : <PlayIcon className={s.tooltipPlayIconSmall} />}
            </button>
            <button
              className={s.tooltipPlayBtnSmall}
              title="Slow play example"
              disabled={!hasAudio || isExampleLoading}
              onClick={(e) => { e.stopPropagation(); onPlayExample(wordId, 0.7); }}
            >
              {isExampleLoading
                ? <SpinnerIcon className={`${s.tooltipPlayIconSmall} ${s.spinning}`} />
                : <SlowPlayIcon className={s.tooltipPlayIconSmall} />}
            </button>
          </div>
          <p className={s.tooltipExample}>{wordData.example}</p>
          <p className={s.tooltipExampleTrans}>{wordData.exampleTranslation}</p>
        </div>
      </div>

      {/* caret */}
      <div className={s.tooltipCaret} />
    </div>,
    document.body
  );
}

function useTooltipPos(ref, show) {
  const [pos, setPos] = useState({ top: 0, left: 0 });
  useEffect(() => {
    if (show && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setPos({ top: rect.top - 10, left: rect.left + rect.width / 2 });
    }
  }, [show, ref]);
  return pos;
}

function WordWithTooltip({ wordData, pinnedWord, onPinWord, onHoverWord, onPlayWord, onPlayExample, loadingWordId }) {
  const [hovered, setHovered] = useState(false);
  const wordRef = useRef(null);

  const isPinned =
    pinnedWord &&
    pinnedWord.word === wordData.word &&
    pinnedWord._sentenceId === wordData._sentenceId;

  const showTooltip = isPinned || hovered;
  const pos = useTooltipPos(wordRef, showTooltip);

  const handleClose = useCallback(() => {
    setHovered(false);
    onPinWord(null);
  }, [onPinWord]);

  const handleClick = useCallback(() => {
    if (isPinned) {
      handleClose();
    } else {
      onPinWord(wordData);
      if (wordData.id) onPlayWord(wordData.id, 1.0);
    }
  }, [isPinned, wordData, onPinWord, handleClose, onPlayWord]);

  const handleMouseEnter = useCallback(() => {
    setHovered(true);
    onHoverWord(wordData);
  }, [wordData, onHoverWord]);

  const handleMouseLeave = useCallback(() => {
    setHovered(false);
    onHoverWord(null);
  }, [onHoverWord]);

  return (
    <span
      ref={wordRef}
      className={`${s.word} ${isPinned ? s.wordActive : ""}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
    >
      {wordData.word}
      {showTooltip && (
        <Tooltip
          wordData={wordData}
          position={pos}
          pinned={isPinned}
          onClose={handleClose}
          onPlayWord={onPlayWord}
          onPlayExample={onPlayExample}
          loadingWordId={loadingWordId}
        />
      )}
    </span>
  );
}

function renderSentenceWords(sentence, pinnedWord, onPinWord, onHoverWord, onPlayWord, onPlayExample, loadingWordId) {
  // Build a lookup from lowercase word to word data
  const wordMap = {};
  for (const w of sentence.words) {
    wordMap[w.word.toLowerCase()] = w;
  }

  // Split on whitespace to keep punctuation attached to words
  const tokens = sentence.in_swedish.split(/\s+/).filter(Boolean);

  return tokens.map((token, i) => {
    // Strip punctuation to find the word core
    const wordCore = token.replace(/[^\p{L}\p{N}]/gu, "");
    const wordData = wordCore ? wordMap[wordCore.toLowerCase()] : null;

    if (wordData) {
      const tagged = { ...wordData, _sentenceId: sentence.sequence_number };
      return (
        <span key={i}>
          {i > 0 && " "}
          <WordWithTooltip
            wordData={{ ...tagged, word: token }}
            pinnedWord={pinnedWord}
            onPinWord={onPinWord}
            onHoverWord={onHoverWord}
            onPlayWord={onPlayWord}
            onPlayExample={onPlayExample}
            loadingWordId={loadingWordId}
          />
        </span>
      );
    }

    // No word info - render as plain text
    return <span key={i}>{i > 0 && " "}{token}</span>;
  });
}

function HighlightedEnglish({ text, meaning }) {
  if (!meaning) return text;

  // Use word boundaries so "a" doesn't match inside "Salman"
  const escaped = meaning.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const regex = new RegExp(`\\b${escaped}\\b`, "i");
  const match = text.match(regex);
  if (!match) return text;

  const idx = match.index;
  return (
    <>
      {text.slice(0, idx)}
      <span className={s.highlightedMeaning}>{text.slice(idx, idx + match[0].length)}</span>
      {text.slice(idx + match[0].length)}
    </>
  );
}

function QuizOverlay({ quizzes, onClose, onPlayAudio }) {
  const [revealedAnswers, setRevealedAnswers] = useState({});

  const toggleAnswer = useCallback((idx) => {
    setRevealedAnswers((prev) => ({ ...prev, [idx]: !prev[idx] }));
  }, []);

  return createPortal(
    <div className={s.quizBackdrop} onClick={onClose}>
      <div
        className={s.quizOverlay}
        onClick={(e) => e.stopPropagation()}
      >
        {/* gold accent stripe */}
        <div className={s.tooltipAccent} />

        <div className={s.quizOverlayBody}>
          <div className={s.quizOverlayHeader}>
            <span className={s.quizOverlayTitle}>Quiz</span>
            <span className={s.quizOverlayCount}>{quizzes.length} questions</span>
            <button className={s.tooltipCloseBtn} onClick={onClose} title="Close">
              <svg className={s.tooltipCloseIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <path d="M6 18 18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className={s.quizList}>
            {quizzes.map((quiz, idx) => (
              <div key={idx} className={s.quizCard}>
                <div className={s.quizQuestionRow}>
                  <div className={s.quizQuestionNum}>Q{idx + 1}</div>
                  {quiz.id && (
                    <button
                      className={s.quizPlayBtn}
                      title="Play question"
                      onClick={() => onPlayAudio(`quizzes/${quiz.id}/question_in_swedish.mp3`)}
                    >
                      <PlayIcon className={s.quizPlayIcon} />
                    </button>
                  )}
                </div>
                <p className={s.quizQuestionSv}>{quiz.question_in_swedish}</p>
                <p className={s.quizQuestionEn}>{quiz.question_in_english}</p>

                {revealedAnswers[idx] ? (
                  <div className={s.quizAnswerRevealed}>
                    <div className={s.quizAnswerSection}>
                      <div className={s.quizAnswerRow}>
                        <span className={s.quizAnswerLabel}>Response</span>
                        {quiz.id && (
                          <button
                            className={s.quizPlayBtnSmall}
                            title="Play response"
                            onClick={() => onPlayAudio(`quizzes/${quiz.id}/long_response.mp3`)}
                          >
                            <PlayIcon className={s.quizPlayIconSmall} />
                          </button>
                        )}
                      </div>
                      <p className={s.quizAnswerText}>{quiz.long_response}</p>
                    </div>
                    <button
                      className={s.quizToggleBtn}
                      onClick={() => toggleAnswer(idx)}
                    >
                      Hide Answer
                    </button>
                  </div>
                ) : (
                  <button
                    className={s.quizShowBtn}
                    onClick={() => toggleAnswer(idx)}
                  >
                    Show Answer
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function AchievementToast({ message }) {
  return createPortal(
    <div className={s.achievementToast}>
      <div className={s.achievementIcon}>
        <StarIcon className={s.masteredCheckIcon} />
      </div>
      <div className={s.achievementText}>
        <span className={s.achievementLabel}>Achievement</span>
        <span className={s.achievementMsg}>{message}</span>
      </div>
    </div>,
    document.body
  );
}

function EmptyState() {
  return (
    <div className={s.emptyState}>
      <svg className={s.emptyIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <p className={s.emptyTitle}>No Story Yet</p>
      <p className={s.emptyDesc}>
        Describe your story in the workshop panel and click "Generate Story" to get started.
      </p>
    </div>
  );
}

function LoadingState() {
  return (
    <div className={s.emptyState}>
      <div className={s.spinner} />
      <p className={s.emptyTitle}>Generating Story...</p>
      <p className={s.emptyDesc}>
        Crafting your Swedish story. This may take a moment.
      </p>
    </div>
  );
}

export default function StoryPanel({ story, loadingAction, areWordsInfoGenerated, areQuizzesGenerated, areAudioLessonGenerated, areAudioQuizzesGenerated, hasAvatar }) {
  const [pinnedWord, setPinnedWord] = useState(null);
  const [hoveredWord, setHoveredWord] = useState(null);
  const [activeQuiz, setActiveQuiz] = useState(null);
  const [masteredSentences, setMasteredSentences] = useState(new Set());
  const [achievement, setAchievement] = useState(null);
  const { play, stop } = useAudioPlayer();
  const { playWord, stop: stopWord, loadingWordId } = useWordAudio();

  useEffect(() => {
    setPinnedWord(null);
    setHoveredWord(null);
    setActiveQuiz(null);
    setMasteredSentences(new Set());
    stop();
    stopWord();
  }, [story, stop, stopWord]);

  const showAchievement = useCallback((msg) => {
    setAchievement(msg);
    setTimeout(() => setAchievement(null), 3000);
  }, []);

  const toggleMastered = useCallback((seqNum) => {
    setMasteredSentences((prev) => {
      const next = new Set(prev);
      if (next.has(seqNum)) {
        next.delete(seqNum);
      } else {
        next.add(seqNum);
        const total = story?.sentences?.length || 0;
        const newCount = next.size;
        if (newCount === total && total > 0) {
          showAchievement("Story Complete! All sentences mastered!");
        } else if (newCount === Math.ceil(total / 2) && total > 2) {
          showAchievement("Halfway there! Keep going!");
        }
      }
      return next;
    });
  }, [story, showAchievement]);

  const handleQuizClick = useCallback((sentence) => {
    setActiveQuiz((prev) =>
      prev === sentence.sequence_number ? null : sentence.sequence_number
    );
  }, []);

  const playAudio = useCallback((audioPath, playbackRate = 1.0) => {
    if (!areAudioLessonGenerated) { alert("Audio not generated yet."); return; }
    play(story.story_id, audioPath, playbackRate);
  }, [areAudioLessonGenerated, play, story]);

  const playQuizAudio = useCallback((audioPath, playbackRate = 1.0) => {
    if (!areAudioQuizzesGenerated) { alert("Quiz audio not generated yet."); return; }
    play(story.story_id, audioPath, playbackRate);
  }, [areAudioQuizzesGenerated, play, story]);

  const handlePlayWord = useCallback((wordId, playbackRate = 1.0) => {
    playWord(wordId, "word_in_swedish.mp3", playbackRate);
  }, [playWord]);

  const handlePlayExample = useCallback((wordId, playbackRate = 1.0) => {
    playWord(wordId, "example_in_swedish.mp3", playbackRate);
  }, [playWord]);

  if (loadingAction === "generate-story" || loadingAction === "build-story" || loadingAction === "load-story") {
    return <div className={s.panel}><LoadingState /></div>;
  }

  if (!story) {
    return <div className={s.panel}><EmptyState /></div>;
  }

  const { topic, sentences } = story;
  const masteredCount = masteredSentences.size;
  const totalCount = sentences.length;
  const progressPct = totalCount > 0 ? (masteredCount / totalCount) * 100 : 0;
  const allMastered = masteredCount === totalCount && totalCount > 0;

  return (
    <div className={s.panel}>
      <div className={s.topicHeader}>
        <div className={s.topicBorderGlow} />

        {hasAvatar && (
          <div className={s.avatarWrap}>
            <img
              className={s.avatarImg}
              src={`${BACKEND}/api/v1/admin/story-avatar/${story.story_id}?t=${Date.now()}`}
              alt="Story avatar"
            />
          </div>
        )}

        <div className={s.topicMeta}>
          <span className={s.sentenceCount}>
            {sentences.length} sentences
          </span>
        </div>
        <h1 className={s.topicTitle}>{topic.in_swedish}</h1>
        <p className={s.topicTranslation}>{topic.in_english}</p>

        {/* Progress tracker */}
        <div className={s.progressSection}>
          <div className={s.progressHeader}>
            <span className={s.progressLabel}>Quest Progress</span>
            <span className={s.progressCount}>
              {masteredCount} / {totalCount}
            </span>
          </div>
          <div className={s.progressTrack}>
            <div
              className={`${s.progressFill} ${allMastered ? s.progressComplete : ""}`}
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>

        <div className={s.headerActions}>
          <button
            className={s.playAll}
            title="Play topic in Swedish"
            disabled={!areAudioLessonGenerated}
            onClick={() => playAudio("topic/topic_in_swedish.mp3")}
          >
            <PlayIcon className={s.playAllIcon} />
            <span>Play Topic</span>
          </button>
        </div>
      </div>

      <div className={s.sentences}>
        {sentences.map((sentence) => {
          const isMastered = masteredSentences.has(sentence.sequence_number);
          return (
            <div
              key={sentence.sequence_number}
              className={`${s.sentenceRow} ${isMastered ? s.sentenceRowMastered : ""}`}
            >
              <div
                className={`${s.sentenceNum} ${isMastered ? s.sentenceNumMastered : ""}`}
                onClick={() => toggleMastered(sentence.sequence_number)}
                title={isMastered ? "Unmark as mastered" : "Mark as mastered"}
              >
                {isMastered ? (
                  <div className={s.masteredCheck}>
                    <CheckIcon className={s.masteredCheckIcon} />
                  </div>
                ) : (
                  String(sentence.sequence_number).padStart(2, "0")
                )}
              </div>
              <div className={s.sentenceContent}>
                <p className={s.swedish}>
                  {areWordsInfoGenerated && sentence.words
                    ? renderSentenceWords(sentence, pinnedWord, setPinnedWord, setHoveredWord, handlePlayWord, handlePlayExample, loadingWordId)
                    : sentence.in_swedish}
                </p>
                <p className={s.english}>
                  {(() => {
                    const activeWord = hoveredWord || pinnedWord;
                    const wordInEnglish = activeWord && activeWord._sentenceId === sentence.sequence_number
                      ? activeWord.wordInEnglish
                      : null;
                    return (
                      <HighlightedEnglish text={sentence.in_english} meaning={wordInEnglish} />
                    );
                  })()}
                </p>
                <div className={s.sentenceActions}>
                  <button
                    className={s.actionBtn}
                    title="Play"
                    disabled={!areAudioLessonGenerated}
                    onClick={() => playAudio(`sentences/${sentence.id}/sentence_in_swedish.mp3`)}
                  >
                    <PlayIcon className={s.actionIcon} />
                    <span className={s.actionLabel}>Play</span>
                  </button>
                  <button
                    className={s.actionBtn}
                    title="Slow play"
                    disabled={!areAudioLessonGenerated}
                    onClick={() => playAudio(`sentences/${sentence.id}/sentence_in_swedish.mp3`, 0.7)}
                  >
                    <SlowPlayIcon className={s.actionIcon} />
                    <span className={s.actionLabel}>Slow</span>
                  </button>
                  <button
                    className={`${s.actionBtn} ${s.actionBtnQuiz}`}
                    title="Quiz"
                    disabled={!areQuizzesGenerated}
                    onClick={() => handleQuizClick(sentence)}
                  >
                    <QuizIcon className={s.actionIcon} />
                    <span className={s.actionLabel}>Quiz</span>
                  </button>
                  <button
                    className={`${s.actionBtn} ${isMastered ? s.actionBtnMastered : s.actionBtnMaster}`}
                    title={isMastered ? "Unmark mastered" : "Mark as mastered"}
                    onClick={() => toggleMastered(sentence.sequence_number)}
                  >
                    <CheckIcon className={s.actionIcon} />
                    <span className={s.actionLabel}>{isMastered ? "Mastered" : "Master"}</span>
                  </button>
                </div>
                {activeQuiz === sentence.sequence_number && sentence.quizzes && sentence.quizzes.length > 0 && (
                  <QuizOverlay
                    quizzes={sentence.quizzes}
                    onClose={() => setActiveQuiz(null)}
                    onPlayAudio={playQuizAudio}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>

      {achievement && <AchievementToast message={achievement} />}
    </div>
  );
}
